import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { sb, Storage } from '../modules/state';

interface Profile {
  id?: string;
  user_id?: string;
  email?: string;
  name?: string;
  last_name?: string;
  birthdate?: string;
  sex?: 'male' | 'female';
  country?: string;
  insulin_confirmed?: boolean;
  medical_consent?: boolean;
  trial_used_at?: string;
  subscription_status?: 'free' | 'trial' | 'active' | 'expired';
  start_weight_kg?: number;
  goal_weight_kg?: number;
  preferred_weight_unit?: 'kg' | 'lbs';
  preferred_measure_unit?: 'cm' | 'in';
  doctor_name?: string;
  doctor_email?: string;
  insulin_gate_passed?: boolean;
  irf_candidate?: boolean;
  current_protocol?: string;
  protocol_start_date?: string;
  created_at?: string;
}

interface BloodData {
  [key: string]: {
    value: number;
    unit: string;
    flag?: 'H' | 'L' | 'normal';
    test_date?: string;
  };
}

interface AppState {
  // Auth
  currentUser: any;
  profile: Profile;

  // Weights
  currentWeight: number | null;
  goalWeight: number | null;
  startWeight: number | null;

  // Timer
  fastHours: number;
  eatHours: number;
  currentProtocolName: string;
  currentProtocolMode: string;

  // Stats
  streak: number;
  completedFasts: number;

  // Coach
  coachUnlocked: boolean;
  chatHistory: Array<{ role: 'user' | 'assistant'; content: string }>;

  // Blood
  bioSex: 'male' | 'female';
  bloodData: BloodData;
  insulinEntered: boolean;
  seenFatBurningKiln: boolean;
  metabolicResetComplete: boolean;

  // Subscription
  subscriptionStatus: 'free' | 'trial' | 'active' | 'expired';
  trialEndsAt: string | null;

  // Actions
  setCurrentWeight: (weight: number) => void;
  setGoalWeight: (weight: number) => void;
  setStartWeight: (weight: number) => void;
  setProfile: (profile: Partial<Profile>) => void;
  setBioSex: (sex: 'male' | 'female') => void;
  addBloodResult: (marker: string, value: number, unit: string, flag?: 'H' | 'L' | 'normal') => void;
  setInsulinEntered: () => void;
  setCoachUnlocked: () => void;
  setSubscriptionStatus: (status: 'free' | 'trial' | 'active' | 'expired') => void;
}

const StateContext = createContext<AppState | undefined>(undefined);

export function StateProvider({ children }: { children: ReactNode }) {
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [profile, setProfileState] = useState<Profile>({});
  const [currentWeight, setCurrentWeightState] = useState<number | null>(null);
  const [goalWeight, setGoalWeightState] = useState<number | null>(null);
  const [startWeight, setStartWeightState] = useState<number | null>(null);
  const [bioSex, setBioSexState] = useState<'male' | 'female'>('male');
  const [bloodData, setBloodDataState] = useState<BloodData>({});
  const [insulinEntered, setInsulinEnteredState] = useState<boolean>(false);
  const [coachUnlocked, setCoachUnlockedState] = useState<boolean>(false);
  const [subscriptionStatus, setSubscriptionStatusState] = useState<'free' | 'trial' | 'active' | 'expired'>('free');

  // Load saved state from localStorage
  useEffect(() => {
    const savedWeight = Storage.get('currentWeight');
    const savedGoal = Storage.get('goalWeight');
    const savedStart = Storage.get('startWeight');
    const savedInsulin = Storage.get('insulinEntered');

    if (savedWeight) setCurrentWeightState(savedWeight);
    if (savedGoal) setGoalWeightState(savedGoal);
    if (savedStart) setStartWeightState(savedStart);
    if (savedInsulin) setInsulinEnteredState(savedInsulin);
  }, []);

  // Load profile from Supabase when user logs in
  useEffect(() => {
    if (!sb) return;
    if (currentUser?.id) {
      sb.from('profiles')
        .select('*')
        .eq('id', currentUser.id)
        .single()
        .then((response) => {
          if (response.data) {
            // Merge profile with email from Auth context
            const mergedProfile = { ...response.data, email: currentUser.email };
            setProfileState(mergedProfile);
            if (response.data.insulin_confirmed) {
              setInsulinEnteredState(true);
              setCoachUnlockedState(true);
            }
            if (response.data.subscription_status) {
              setSubscriptionStatusState(response.data.subscription_status as any);
            }
          }
        })
        .catch((err) => console.error('[State] Error loading profile:', err));
    } else if (currentUser?.email) {
      // User signed in but no profile yet - create one with email
      setProfileState((prev) => ({ ...prev, email: currentUser.email }));
    }
  }, [currentUser?.id, currentUser?.email]);

  // Update localStorage when weights change
  useEffect(() => {
    if (currentWeight !== null) Storage.set('currentWeight', currentWeight);
    if (goalWeight !== null) Storage.set('goalWeight', goalWeight);
    if (startWeight !== null) Storage.set('startWeight', startWeight);
  }, [currentWeight, goalWeight, startWeight]);

  const setProfile = useCallback((updates: Partial<Profile>) => {
    setProfileState((prev) => ({ ...prev, ...updates }));
  }, []);

  const setCurrentWeight = (weight: number) => {
    setCurrentWeightState(weight);
  };

  const setGoalWeight = (weight: number) => {
    setGoalWeightState(weight);
  };

  const setStartWeight = (weight: number) => {
    setStartWeightState(weight);
  };

  const setBioSex = (sex: 'male' | 'female') => {
    setBioSexState(sex);
  };

  const addBloodResult = (marker: string, value: number, unit: string, flag?: 'H' | 'L' | 'normal') => {
    setBloodDataState((prev) => ({
      ...prev,
      [marker]: { value, unit, flag },
    }));
  };

  const setInsulinEntered = () => {
    if (!sb) return;
    setInsulinEnteredState(true);
    Storage.set('insulinEntered', true);
    if (currentUser?.id) {
      sb.from('profiles')
        .update({ insulin_confirmed: true })
        .eq('id', currentUser.id)
        .then(() => {
          setProfileState((prev) => ({ ...prev, insulin_confirmed: true }));
          setCoachUnlockedState(true);
        });
    }
  };

  const setCoachUnlocked = () => {
    setCoachUnlockedState(true);
  };

  const setSubscriptionStatus = (status: 'free' | 'trial' | 'active' | 'expired') => {
    if (!sb) return;
    setSubscriptionStatusState(status);
    if (currentUser?.id) {
      sb.from('profiles')
        .update({ subscription_status: status })
        .eq('id', currentUser.id)
        .catch((err) => console.error('[State] Error updating subscription:', err));
    }
  };

  const value: AppState = {
    currentUser,
    profile,
    currentWeight,
    goalWeight,
    startWeight,
    fastHours: 16,
    eatHours: 8,
    currentProtocolName: '16:8',
    currentProtocolMode: 'daily',
    streak: 0,
    completedFasts: 0,
    coachUnlocked,
    chatHistory: [],
    bioSex,
    bloodData,
    insulinEntered,
    seenFatBurningKiln: false,
    metabolicResetComplete: false,
    subscriptionStatus,
    trialEndsAt: null,
    setCurrentWeight,
    setGoalWeight,
    setStartWeight,
    setProfile,
    setBioSex,
    addBloodResult,
    setInsulinEntered,
    setCoachUnlocked,
    setSubscriptionStatus,
  };

  return <StateContext.Provider value={value}>{children}</StateContext.Provider>;
}

export function useStateContext() {
  const context = useContext(StateContext);
  if (context === undefined) {
    throw new Error('useStateContext must be used within a StateProvider');
  }
  return context;
}
